
import { useState, useEffect } from 'react';

interface CountdownTimerProps {
  targetDate: string;
  className?: string;
  onComplete?: () => void;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({ 
  targetDate, 
  className = '',
  onComplete
}) => {
  const calculateTimeLeft = (): { hours: number; minutes: number; seconds: number } => {
    const difference = new Date(targetDate).getTime() - new Date().getTime();
    
    if (difference <= 0) {
      if (onComplete) onComplete();
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    
    return {
      hours: Math.floor(difference / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000)
    };
  };
  
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  
  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      
      if (newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
        clearInterval(timer);
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [targetDate]);
  
  const formatTime = (value: number): string => {
    return value.toString().padStart(2, '0');
  };
  
  return (
    <div className={`relative z-10 flex items-center justify-center gap-3 ${className}`}>
      <div className="flex flex-col items-center">
        <div className="bg-bauhaus-darkgreen border-2 border-bauhaus-offwhite/70 rounded-md px-3 py-2 shadow-lg">
          <span className="text-3xl md:text-4xl font-bold text-bauhaus-offwhite font-['Bebas_Neue'] tracking-wider">
            {formatTime(timeLeft.hours)}
          </span>
        </div>
        <span className="text-xs md:text-sm text-bauhaus-offwhite/90 mt-1 uppercase font-medium tracking-wider">hours</span>
      </div>
      
      <span className="text-2xl md:text-3xl font-bold text-bauhaus-offwhite animate-pulse">:</span>
      
      <div className="flex flex-col items-center">
        <div className="bg-bauhaus-darkgreen border-2 border-bauhaus-offwhite/70 rounded-md px-3 py-2 shadow-lg">
          <span className="text-3xl md:text-4xl font-bold text-bauhaus-offwhite font-['Bebas_Neue'] tracking-wider">
            {formatTime(timeLeft.minutes)}
          </span>
        </div>
        <span className="text-xs md:text-sm text-bauhaus-offwhite/90 mt-1 uppercase font-medium tracking-wider">min</span>
      </div>
      
      <span className="text-2xl md:text-3xl font-bold text-bauhaus-offwhite animate-pulse">:</span>
      
      <div className="flex flex-col items-center">
        <div className="bg-bauhaus-darkgreen border-2 border-bauhaus-offwhite/70 rounded-md px-3 py-2 shadow-lg">
          <span className="text-3xl md:text-4xl font-bold text-bauhaus-offwhite font-['Bebas_Neue'] tracking-wider animate-pulse">
            {formatTime(timeLeft.seconds)}
          </span>
        </div>
        <span className="text-xs md:text-sm text-bauhaus-offwhite/90 mt-1 uppercase font-medium tracking-wider">sec</span>
      </div>
    </div>
  );
};
